import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { getDashboardList } from "@/lib/api-client";
import {
  createNewDashboard,
  deleteDashboard,
  getDashboards,
} from "@/lib/api-client";
import toast from "react-hot-toast";
import { dashItemProps } from "@/types";
interface DashboardInput {
  dashboard_id: string;
  dashboard_name: string;
  dashboard_desc: string;
}

interface Dashboard {
  dashboard_id: string;
  dashboard_name: string;
  dashboard_desc: string;
  dash_items: number;
  last_updated: string;
}

export function useDashboard(initialData?: dashItemProps[]) {
  const {
    data: dashboards,
    refetch: refetchDashboard,
    isLoading: isDashboardLoading,
    isFetching: isDashboardFetching,
  } = useQuery({
    queryKey: ["dashboards"],
    queryFn: getDashboards,
    initialData,
    refetchOnWindowFocus: false,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: false,
  });

  const hasDashboards = Array.isArray(dashboards) && dashboards?.length > 0;

  const {
    data: dashboardList,
    isLoading: isDashboardListLoading,
    refetch: refetchDashList,
  } = useQuery({
    queryKey: ["dashboard-list"],
    queryFn: async () => {
      const { dashboards } = await getDashboardList();
      return dashboards;
    },
  });

  const { mutate: createDashboard, isPending: isCreatingDashboard } =
    useMutation({
      mutationFn: (data: DashboardInput) => createNewDashboard(data),

      onSuccess: async () => {
        toast.success("Dashboard Created Successfully", {
          position: "bottom-right",
        });

        // First refetch the dashboard list
        await refetchDashList();
        // Then refetch the main dashboard data
        await refetchDashboard();
      },
      onError: () => {
        toast.error("Failed to create dashboard", {
          position: "bottom-right",
        });
      },
    });

  const { mutate: deleteDash } = useMutation({
    mutationFn: async (dash_id: string) => await deleteDashboard(dash_id),
    onSuccess: async () => {
      toast.success("Dashboard deleted successfully", {
        position: "bottom-right",
      });

      refetchDashboard();
    },
    onError: (context: any) => {
      toast.error("Dashboard delete failed", {
        position: "bottom-right",
      });
    },
  });

  return {
    dashboards,
    dashboardList,
    refetchDashList,
    isDashboardLoading,
    isDashboardListLoading,
    createDashboard,
    isCreatingDashboard,
    isDashboardFetching,
    refetchDashboard,
    deleteDash,
    hasDashboards,
  };
}
